import {
  FetchPolicy,
  NetworkStatus,
  useLazyQuery,
  useQuery,
} from "@apollo/client";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  api,
  BaseLedgerEntryFullFields,
  BaseTransactionFullFields,
} from "src/api";
import { BaseAuditLogFields } from "src/api/fragments";
import {
  Query,
  QueryGetTransactionArgs,
  Transaction,
  TransactionTypeOptionResponse,
} from "src/api/generated/types";
import { Maybe } from "src/core";
import { getPrepopulatedTxns } from "src/redux/reducers/active";
import { isLoadingGQL } from "src/utils/helpers";

export type UseTransactionByIdReturn = {
  ledgerEntries: BaseLedgerEntryFullFields[];
  transaction: Maybe<BaseTransactionFullFields>;
  defaultLabels: TransactionTypeOptionResponse | null;
  auditLogs: BaseAuditLogFields[];
  isLoadingTransaction: boolean;
  isLoadingLedgerEntries: boolean;
  getLedgerEntries: () => Promise<void>;
  loadedType: string;
  isUsingCachedTransaction: boolean;
  isUsingFreshTransaction: boolean;
};

export type GetTxn = {
  resolver: { getTransaction?: BaseTransactionFullFields };
  variables: { transactionId: string };
};

type FetchEntries = {
  resolver: { getTransactionLedgerEntries?: BaseLedgerEntryFullFields[] };
  variables: { transactionId: string };
};

export const useTransactionById = (
  transactionId: Maybe<string>,
  fetchPolicy: FetchPolicy = "no-cache"
): UseTransactionByIdReturn => {
  const txnMapping = useSelector(getPrepopulatedTxns);
  const cachedTxn = txnMapping[transactionId || ""] ?? null;

  const defaultLabels =
    (cachedTxn as unknown as Maybe<Transaction>)?.options || null;
  // if (cachedTxn) {
  //   console.log("hit cache");
  // }

  const variables = useMemo(
    (): QueryGetTransactionArgs => ({ transactionId: transactionId || "" }),
    [transactionId]
  );

  const {
    data: fullTxnData,
    networkStatus,
    refetch: refetchTransaction,
  } = useQuery<GetTxn["resolver"], GetTxn["variables"]>(
    api.transactions.retrieve,
    {
      fetchPolicy,
      variables,
      skip: !variables.transactionId,
    }
  );

  // console.log(cachedTxn, fullTxnData);

  const [
    fetchLedgerEntries,
    { data: ledgerEntryData, loading: isLoadingLedgerEntries },
  ] = useLazyQuery<FetchEntries["resolver"], FetchEntries["variables"]>(
    api.transactions.ledgerEntries,
    {
      fetchPolicy: "no-cache",
    }
  );

  const [
    fetchAuditLogs,
    { error, data: auditLogsData, loading: isLoadingAuditLogs },
  ] = useLazyQuery<Pick<Query, "getAuditLogs">>(api.transactions.getAuditLogs, {
    fetchPolicy: "no-cache",
  });

  console.log(error);

  const _fetchFullTxn = useCallback(async () => {
    if (!transactionId) {
      return;
    }

    await refetchTransaction();
  }, [transactionId]);

  const _fetchLedgerEntries = useCallback(async () => {
    if (!transactionId) {
      return;
    }

    await Promise.all([
      fetchLedgerEntries({
        variables: {
          transactionId,
        },
      }),
    ]);
  }, [transactionId, fetchLedgerEntries]);

  const _fetchAuditLogs = useCallback(async () => {
    if (!transactionId) {
      return;
    }

    await Promise.all([
      fetchAuditLogs({
        variables: {
          transactionId,
        },
      }),
    ]);
  }, [transactionId, fetchAuditLogs]);

  useEffect(() => void _fetchFullTxn(), [_fetchFullTxn]);
  useEffect(() => void _fetchLedgerEntries(), [_fetchLedgerEntries]);
  useEffect(() => void _fetchAuditLogs(), [_fetchAuditLogs]);

  const ledgerEntries = ledgerEntryData?.getTransactionLedgerEntries || [];
  const transaction = fullTxnData?.getTransaction || cachedTxn || null;
  const auditLogs = auditLogsData?.getAuditLogs || [];
  const isLoadingTransaction =
    isLoadingGQL(networkStatus) || networkStatus === NetworkStatus.fetchMore;
  const isUsingCachedTransaction = !fullTxnData?.getTransaction && !!cachedTxn;

  return {
    ledgerEntries,
    loadedType: fullTxnData?.getTransaction ? "full" : "cached",
    transaction: transaction,
    auditLogs,
    defaultLabels,
    isLoadingTransaction: isLoadingTransaction,
    isLoadingLedgerEntries,
    getLedgerEntries: _fetchLedgerEntries,
    isUsingCachedTransaction,
    isUsingFreshTransaction: !!fullTxnData?.getTransaction,
  };
};
